// src/modules/socialhive/mobile/MobileTicketsPage.jsx
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import './MobileTicketsPage.css';
import { TbCaretDownFilled, TbCaretUpFilled, TbAlertTriangle } from 'react-icons/tb'; // <- eklendi
import { useLanguage } from '../../LanguageProvider';
import InputField from '../../components/InputField/InputField';
import { GeneralContext } from '../../GeneralProvider';

// ---- ŞIK UYARI BİLEŞENİ ----
function MtiAlert({ type = 'error', message, onClose }) {
  if (!message) return null;
  return (
    <div
      className={`mti-alert mti-alert-${type}`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="mti-alert-icon">
        <TbAlertTriangle />
      </div>
      <div className="mti-alert-text">{message}</div>
      <button
        className="mti-alert-close"
        onClick={onClose}
        aria-label="Close notification"
      >
        ×
      </button>
    </div>
  );
}

// ---- TicketCard'ı DIŞARI taşıdık ve memo'ladık ----
const TicketCard = React.memo(function TicketCard({
  ticket,
  infos,
  setSafeTicketInfos,
  hasAutofilledOnce,
  setHasAutofilledOnce,
  userData,
  anyAutoFilledExists,
  getTranslation,
}) {
  const {
    ticketID, title, description, price, currency, type,
    trackInventory, inventory, soldInventory
  } = ticket;

  const count = infos.length;
  const remaining = useMemo(() => {
    if (trackInventory === '1') {
      const inv = Number(inventory) || 0;
      const sold = Number(soldInventory) || 0;
      const left = inv - sold;
      return left > 0 ? left : 0;
    }
    return Infinity;
  }, [trackInventory, inventory, soldInventory]);

  const canAdd = count < remaining;

  const [showFullDesc, setShowFullDesc] = useState(false);
  const preview = useMemo(() => {
    if (!description) return '';
    if (description.length <= 220 || showFullDesc) return description;
    return description.slice(0, 220) + '…';
  }, [description, showFullDesc]);

  const increment = () => {
    if (!canAdd) return;

    const shouldAutofill = !anyAutoFilledExists && userData?.email && !hasAutofilledOnce;

    const newInfo = shouldAutofill ? {
      name: userData.name || '',
      lastname: userData.lastname || '',
      email: userData.email || '',
      phone: userData.phone || '',
    } : {
      name: '',
      lastname: '',
      email: '',
      phone: '',
    };

    const nextArr = [
      ...infos,
      {
        ...newInfo,
        ticketType: type,
        ticketTitle: title,
        ticketPrice: price ? Number(price) : 0,
        ticketID,
        channelID: ticket.channelID,
        eventID: ticket.eventID,
      }
    ];

    setSafeTicketInfos(ticketID, nextArr);
    if (shouldAutofill) setHasAutofilledOnce(true);
  };

  const decrement = () => {
    if (count === 0) return;
    const updated = infos.slice(0, -1);
    setSafeTicketInfos(ticketID, updated);
    if (updated.length === 0) setHasAutofilledOnce(false);
  };

  const handleInputChange = (index, field, value) => {
    setSafeTicketInfos(ticketID, (() => {
      const next = [...infos];
      next[index] = { ...next[index], [field]: value };
      return next;
    })());
  };

  const clearRow = (index) => {
    const next = [...infos];
    next[index] = { ...next[index], name: '', lastname: '', email: '', phone: '' };
    setSafeTicketInfos(ticketID, next);
    setHasAutofilledOnce(true);
  };

  const isRowEmpty = (info) => !(info?.name || info?.lastname || info?.email || info?.phone);

  const priceText = price ? `${price} ${currency || 'TRY'}` : getTranslation('free');
  const invLeft = (Number(inventory) || 0) - (Number(soldInventory) || 0);

  return (
    <div className="mti-card">
      <div className="mti-card-head">
        <div className="mti-card-title">
          {title}
          {trackInventory === '1' && (
            <span className="mti-card-chip">
              {invLeft > 0 ? `x${invLeft}` : getTranslation('soldOut')}
            </span>
          )}
        </div>
        <div className="mti-card-price">{priceText}</div>
      </div>

      {description && (
        <div className="mti-card-desc">
          <p>{preview}</p>
          {description.length > 220 && (
            <button type="button" className="mti-moreless" onClick={() => setShowFullDesc(s => !s)}>
              {showFullDesc ? (<>{getTranslation('showLess')} <TbCaretUpFilled /></>) :
                (<>{getTranslation('showMore')} <TbCaretDownFilled /></>)}
            </button>
          )}
        </div>
      )}

      <div className="mti-qty-row">
        <button
          className="mti-qty-btn"
          onClick={decrement}
          disabled={count === 0}
          title={count === 0 ? (getTranslation('nothingToRemove') || '') : undefined}
        >−</button>
        <span className="mti-qty-count">{count}</span>
        <button
          className="mti-qty-btn primary"
          onClick={increment}
          disabled={!canAdd}
          title={!canAdd && trackInventory === '1' ? (getTranslation('noMoreInventory') || '') : undefined}
        >+</button>
      </div>

      {infos.map((info, idx) => {
        const empty = isRowEmpty(info);
        return (
          <div className="mti-attendee" key={`${ticketID}-${idx}`}>
            <div className="mti-attendee-top">
              <span className="mti-attendee-title">{getTranslation('attendeeInfoForTicket')} #{idx + 1}</span>
              <button
                type="button"
                className="mti-attendee-clear"
                onClick={() => clearRow(idx)}
                disabled={empty}
                title={empty ? (getTranslation('nothingToRemove') || '') : (getTranslation('clear') || 'Temizle')}
              >
                {getTranslation('clear')}
              </button>
            </div>

            <div className="mti-grid">
              <InputField
                style={{ backgroundColor: 'transparent', border: '1px solid var(--mti-border)', fontSize: '16px' }}
                placeholder={`${getTranslation('name')} *`}
                value={info.name}
                onChange={e => handleInputChange(idx, 'name', e.target.value)}
                autoComplete="on"
                autoCorrect="on"
                autoCapitalize="sentences"
              />
              <InputField
                style={{ backgroundColor: 'transparent', border: '1px solid var(--mti-border)', fontSize: '16px' }}
                placeholder={`${getTranslation('lastname')} *`}
                value={info.lastname}
                onChange={e => handleInputChange(idx, 'lastname', e.target.value)}
                autoComplete="on"
                autoCorrect="on"
                autoCapitalize="sentences"
              />
            </div>
            <div className="mti-grid">
              <InputField
                style={{ backgroundColor: 'transparent', border: '1px solid var(--mti-border)', fontSize: '16px' }}
                placeholder={`${getTranslation('email')} *`}
                value={info.email}
                onChange={e => handleInputChange(idx, 'email', e.target.value)}
                autoComplete="email"
                autoCorrect="off"
                autoCapitalize="none"
                type="email"
              />
              <InputField
                style={{ backgroundColor: 'transparent', border: '1px solid var(--mti-border)', fontSize: '16px' }}
                placeholder={`${getTranslation('phone')} *`}
                maxLength={10}
                value={info.phone}
                onChange={e => handleInputChange(idx, 'phone', e.target.value)}
                autoComplete="tel"
                autoCorrect="off"
                autoCapitalize="none"
                inputMode="tel"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
});

export default function MobileTicketsPage({
  event,
  onBack,
  onProceedPayment,
}) {
  const { getTranslation } = useLanguage();
  const { setSuppressInvitationPolling } = useContext(GeneralContext);

  const [ticketInfosMap, setTicketInfosMap] = useState({});
  const [hasAutofilledOnce, setHasAutofilledOnce] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ---- UI uyarı durumu ----
  const [uiError, setUiError] = useState(null);

  const sessionUserString = sessionStorage.getItem('user');
  const localUserString = localStorage.getItem('user');
  let userData = null;
  try {
    userData = sessionUserString ? JSON.parse(sessionUserString) :
      localUserString ? JSON.parse(localUserString) : null;
  } catch {
    userData = null;
  }

  useEffect(() => {
    setSuppressInvitationPolling(true);

    return () => {
      setSuppressInvitationPolling(false);
    };
  }, [setSuppressInvitationPolling]);

  useEffect(() => {
    const isiOSDevice = (() => {
      const ua = navigator.userAgent || navigator.vendor || window.opera || "";
      const platform = navigator.platform || "";
      const iDevice = /iPad|iPhone|iPod/.test(ua);
      const iPadOS13plus = /MacIntel/.test(platform) && (navigator.maxTouchPoints || 0) > 1;
      return iDevice || iPadOS13plus;
    })();

    if (!isiOSDevice) return;
    document.documentElement.classList.add('is-ios');

    const setVars = () => {
      const vv = window.visualViewport;
      const vvh = Math.round(vv ? vv.height : window.innerHeight);
      const kb = vv ? Math.max(0, window.innerHeight - vvh) : 0;
      document.documentElement.style.setProperty('--vvh', `${vvh}px`);
      document.documentElement.style.setProperty('--keyboard-offset', `${kb}px`);
    };

    setVars();
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setVars);
      window.visualViewport.addEventListener('scroll', setVars);
    }

    const onFocus = (e) => {
      const el = e.target;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.getAttribute('contenteditable') === 'true')) {
        setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
      }
    };
    document.addEventListener('focusin', onFocus, { passive: true });

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', setVars);
        window.visualViewport.removeEventListener('scroll', setVars);
      }
      document.removeEventListener('focusin', onFocus);
    };
  }, []);

  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      document.body.classList.add('ios-fix');
    } else {
      document.body.classList.remove('ios-fix');
    }
  }, []);

  // Sayfanın en üst seviyede (component) içinde:
  useEffect(() => {
    const prev = document.body.style.overflow;
    // açılışta olası kilidi kaldır
    document.body.style.overflow = '';
    return () => {
      // bu sayfa çıkarken önceki değeri geri ver
      document.body.style.overflow = prev;
    };
  }, []);

  const handleProceed = () => {
    const allInfos = Object.values(ticketInfosMap).flat();
    if (allInfos.length === 0) return;

    const invalid = allInfos.find(info =>
      !info.name?.trim() ||
      !info.lastname?.trim() ||
      !info.email?.trim() ||
      !info.phone?.trim() ||
      info.phone.replace(/\D/g, '').length < 10
    );

    if (invalid) {
      const msg =
        getTranslation('pleaseFillAllFields') ||
        'Lütfen tüm zorunlu alanları doldurun (telefon dahil).';

      setUiError(msg);
      window.clearTimeout(window.__mtiErrTimer);
      window.__mtiErrTimer = window.setTimeout(() => setUiError(null), 3000);
      return;
    }

    // İşlem başlarken loading başlasın
    setIsLoading(true);

    // onProceedPayment işlem tamamlayınca loading kapatılmalı
    Promise.resolve(onProceedPayment?.(subTotal, ticketInfosMap))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!event?.tickets) return;
    const m = {};
    event.tickets.forEach(t => { m[t.ticketID] = []; });
    setTicketInfosMap(m);
  }, [event?.tickets]);

  const subTotal = useMemo(() => {
    if (!ticketInfosMap) return 0;
    return Object.values(ticketInfosMap)
      .flat()
      .reduce((sum, it) => sum + (Number(it.ticketPrice) || 0), 0);
  }, [ticketInfosMap]);

  const validateUnique = (nextMap) => {
    const emails = new Set();
    const phones = new Set();
    for (const arr of Object.values(nextMap)) {
      for (const { email, phone } of arr) {
        if (email) {
          if (emails.has(email)) return { ok: false, field: 'email' };
          emails.add(email);
        }
        if (phone) {
          const p = (phone || '').trim();
          if (p) {
            if (phones.has(p)) return { ok: false, field: 'phone' };
            phones.add(p);
          }
        }
      }
    }
    return { ok: true };
  };

  const setSafeTicketInfos = useCallback((ticketID, arr) => {
    setTicketInfosMap(prev => {
      const next = { ...prev, [ticketID]: arr };
      const v = validateUnique(next);
      if (!v.ok) return prev;
      return next;
    });
  }, []);

  const anyAutoFilledExists = useMemo(() => {
    return Object.values(ticketInfosMap).some(infos =>
      infos.some(info => info.email || info.name || info.lastname)
    );
  }, [ticketInfosMap]);

  return (
    <div className="mti-page">
      {/* ŞIK UYARI */}
      <MtiAlert
        type="error"
        message={uiError}
        onClose={() => setUiError(null)}
      />

      <header className="mti-top">
        <button className="mti-back" onClick={onBack} aria-label="Back">‹</button>
        <h1 className="mti-title">{event?.title}</h1>
      </header>

      {event?.cover && (
        <div className="mti-cover">
          <img src={event.cover} alt={event?.title} />
        </div>
      )}

      <div className="mti-section">
        <h2 className="mti-section-h">{getTranslation('tickets') || 'Etkinlik Biletleri:'}</h2>

        {Array.isArray(event?.tickets) && event.tickets
          .slice()
          .sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0))
          .map(t => (
            <TicketCard
              key={t.ticketID}
              ticket={t}
              infos={ticketInfosMap[t.ticketID] || []}
              setSafeTicketInfos={setSafeTicketInfos}
              hasAutofilledOnce={hasAutofilledOnce}
              setHasAutofilledOnce={setHasAutofilledOnce}
              userData={userData}
              anyAutoFilledExists={anyAutoFilledExists}
              getTranslation={getTranslation}
            />
          ))}
      </div>

      <div className="mti-cta">
        <div className="mti-cta-left">
          <div className="mti-cta-caption">{getTranslation('myTickets') || 'Biletin:'}</div>
          <div className="mti-cta-total">{subTotal} {event?.tickets?.[0]?.currency || 'TRY'}</div>
        </div>
        <button
          className={`mti-cta-btn ${isLoading ? 'loading' : ''}`}
          onClick={handleProceed}
          disabled={
            isLoading || (subTotal <= 0 && Object.values(ticketInfosMap).flat().length === 0)
          }
        >
          {isLoading ? '...' : getTranslation('buyTicket') || 'Biletini Al'}
        </button>
      </div>
    </div>
  );
}